import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Box, Flex, Icon, Copy, Image, themeColor, HorizontalRule, Modal } from '@elparking/components'
import styled from 'styled-components'
import FreeCancel from '../FreeCancel'
import { CarFeatures } from '../CarFeatures'
import SelectPriceBlock from './SelectPriceBlock'
import Features from './Features'
import Recommended from './Recommended'
import MoreInfoLink from './MoreInfoLink'
import RateList from './RateList'
import RateBox from './RateBox'
import NoPayment from '../NoPayment'
import PriceBlock from './PriceBlock'
import { RentalCarEvents, trackTealiumEvent } from 'commons/js/services/analytics'

const Container = styled.div`
    border-radius: 3px 3px 20px 3px;
    padding: 1em;
    border: 1px solid ${themeColor('gray30')};
    box-shadow: 0px 2px 4px 0px rgba(112,112,112,0.16);
`

const ContentDesktop = styled(Box)`
`
const ContentMobile = styled(ContentDesktop)`
  cursor: pointer;
    transition: transform 0.2s ease-in-out;

    &:hover {
        transform: scale(1.05);
        padding: 0px 15px;
    }
`

const CardTitle = styled(Copy)`
    flex-grow: 1;
    `
const Card = ({
    id,
    car,
    place,
    seller,
    freeCancel,
    onClick,
    isMutuaUser,
    setOnCloseModal,
    screenViewRollBack,
}) => {
    const [linkModalInfo, setLinkModalInfo] = useState(null)
    const rates = car.rates
    useEffect(() => {
        setOnCloseModal(linkModalInfo
            ? () => setLinkModalInfo(null)
            : null)
    }, [linkModalInfo, setLinkModalInfo, setOnCloseModal])

    const onOpenModalInfo = () => {
        setLinkModalInfo(rates[0].additionalInfoUrl)
        trackTealiumEvent(RentalCarEvents.showMoreInfoPopUpView(seller.name.toLowerCase()))
    }

    const onCloseModalInfo = () => {
        setLinkModalInfo(null)
        screenViewRollBack()
    }

    return <Container>
        <ContentDesktop display={['none', 'none', 'flex']} width='100%'>
            <Flex alignItems={rates.length === 1 ? 'center' : 'start'} width='100%'>
                <Box width='200px' pR='10px' textAlign='center'>
                    <Box mb='10px'>
                        <Image
                          onError={({ currentTarget }) => {
                                currentTarget.onerror = null
                                currentTarget.src = '/images/rental-car/default_car.webp'
                            }}
                          width='100%'
                          minHeight='100px'
                          height='auto'
                          alt={car.type}
                          src={car.image} />
                    </Box>
                    <Box mb='5px' mt='5px' textAlign='center'>
                        <Box display='inline-flex' alignItems='center'>
                            <Image height={['15px', '18px']} alt={seller.name} src={seller.image} />
                            <MoreInfoLink openModalInfo={() => onOpenModalInfo()} />
                        </Box>
                    </Box>
                    {place && <Flex flexDirection='row' alignItems='start'>
                        <Copy padding='0' fontSize='xxSmall'>
                            <Icon
                              fontColor='gray80'
                              type='location'
                              fontWeight='medium'
                              fontSize='xxSmall' />
                        </Copy>
                        <Box ml='4px'>
                            <Copy padding='0' textAlign='left' fontSize='xxSmall' fontColor='gray90' fontWeight='medium'>
                                <FormattedMessage defaultMessage='{metres}m de {place}' values={{
                                metres: place.distance,
                                place: place.name,
                            }} />
                            </Copy>
                            <Copy textAlign='left' padding='0' fontSize='xxSmall' fontColor='gray90' fontWeight='regular'>{place.originStationName}</Copy>
                        </Box>
                    </Flex>}
                </Box>
                <Box width='calc(100% - 160px)' >
                    <Flex mb='1em' ml={['0', '1em']} alignItems='baseline'>
                        <Copy
                          as='h2'
                          padding='0'
                          fontColor='accent_dark'
                          fontSize='xxLarge'
                          fontWeight='medium'
  >
                            {car.type}
                        </Copy>

                        <Copy
                          as='h3'
                          padding='0 0 0 0.5em'
                          fontSize='medium'
                          fontWeight='regular'
                          fontColor='accent_dark'
  >
                            {car.description}
                        </Copy>
                    </Flex>
                    <Flex width='100%'>
                        <Box width={(rates.length === 1) ? 'calc(100% - 160px)' : '100%'}>
                            <Box paddingLeft='1em'>
                                <Box>
                                    <CarFeatures {...car.features} />
                                </Box>
                                {(rates.length === 1) && <Box>
                                    <HorizontalRule />
                                </Box>}
                                <Flex mb='10px' gridGap='10px'>
                                    {freeCancel && <FreeCancel />}
                                    <NoPayment />
                                </Flex>
                                {(rates.length === 1)
                                    ? <Features features={rates[0].features} />
                                    : <Flex width='100%' justifyContent='space-between'>
                                        {rates.map((rate, index) => {
                                            const highlighted = (rates.length - 1) === index
                                            return <RateBox
                                              borderColor='gray10'
                                              p='0 20px'
                                              fontSize='small'
                                              width='calc(50% - 10px)'
                                              key={`rate_${id}_${index}`}>
                                                <Flex width='100%' height='100%' justifyContent='space-between' flexDirection='column'>
                                                    <Box w='100%'>
                                                        <Copy fontSize='medium' mt='10px' fontWeight='bold' fontColor='accent_dark'>
                                                            {rate.name} {highlighted && <Recommended />}
                                                        </Copy>
                                                        <Features features={rate.features} />
                                                    </Box>
                                                    <Box mt='20px' mb='10px' width='100%'>
                                                        <SelectPriceBlock
                                                          rate={rate}
                                                          highlighted={highlighted}
                                                          flexDirection='row'
                                                          textAlign='left'
                                                          onClick={onClick}
                                                        />
                                                    </Box>
                                                </Flex>
                                            </RateBox>
                                        })}
                                    </Flex>
                                }
                            </Box>
                        </Box>
                        {(rates.length === 1) && <Box width='160px' paddingLeft='5px'>
                            <SelectPriceBlock flexDirection='column' rate={rates[0]} onClick={onClick} isMutuaUser={isMutuaUser} />
                        </Box>}
                    </Flex>
                </Box>
            </Flex>
        </ContentDesktop>
        <ContentMobile display={['flex', 'flex', 'none']} width='100%' onClick={() => (rates.length === 1) && onClick(rates[0])}>
            <Flex flexDirection='column' width='100%'>
                <Flex mb='2em' flexDirection={['column', 'row']}>
                    <Flex w='100%' flexDirection={['row', 'column']} flexWrap='wrap'>
                        <CardTitle as='h2' padding='0' fontColor='accent_dark' fontSize='xxLarge' fontWeight='medium' width='auto'>
                            {car.type}
                        </CardTitle>
                        <CardTitle as='h3' padding={['0.3em 0 0 0', '0.3em 0 0 0']} fontSize='medium' fontWeight='light' fontColor='accent_dark' width='auto'>
                            {car.description}
                        </CardTitle>
                    </Flex>
                    <Flex w='100%' textAlign='right' flexDirection={['row', 'column']} padding={['0.5em 0 0 0', '0em']}>
                        {freeCancel && <Box mb='10px' mr={['0.5em', '0']}><FreeCancel /></Box>}
                        <Box mb='10px'><NoPayment /></Box>
                    </Flex>
                </Flex>
                <Box width='100%'>
                    <Flex width='100%'>
                        <Box width='125px'>
                            <Image
                              onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = '/images/rental-car/default_car.webp'
                                }}
                              width='110px'
                              height='75px'
                              alt={seller.name}
                              src={car.image} />
                        </Box>
                        <Box width='calc(100% - 125px)'>
                            <CarFeatures {...car.features} />
                        </Box>
                    </Flex>
                </Box>
                <Box>
                    <HorizontalRule />
                </Box>
                {(rates.length === 1) && <Box>
                    <Features features={rates[0].features} />
                </Box>}
                <Flex alignItems='end' mt='0em' mb='0.5em'>
                    <Flex flexDirection={(rates.length === 1) ? 'column' : 'row'} width={(rates.length === 1) ? '50%' : '100%'}>
                        <Flex mb='5px' pr='20px' width='fit-content' alignItems='center'>
                            <Image height='20px' alt={seller.name} src={seller.image} />
                            <MoreInfoLink openModalInfo={() => onOpenModalInfo()} />
                        </Flex>
                        {place && <Flex width='100%' flexDirection='row' alignItems='start'>
                            <Copy padding='0' fontSize='xxSmall'>
                                <Icon
                                  fontColor='gray80'
                                  type='location'
                                  fontWeight='medium'
                                  fontSize='xxSmall' />
                            </Copy>
                            <Box maxWidth={(rates.length === 1) ? '100%' : '80%'} ml='4px'>
                                <Copy.truncate lines='1' padding='0' textAlign='left' fontSize='xxSmall' fontColor='gray90' fontWeight='medium'>
                                    <FormattedMessage defaultMessage='{metres}m de {place}' values={{
                                metres: place.distance,
                                place: place.name,
                            }} />
                                </Copy.truncate>
                                <Copy.truncate lines='1' textAlign='left' padding='0' fontSize='xxSmall' fontColor='gray90' fontWeight='regular'>{place.originStationName}</Copy.truncate>
                            </Box>
                            </Flex>
                        }
                    </Flex>
                    {(rates.length === 1) && <Box textAlign='right' width='50%'>
                        <Flex alignItems='end' justifyContent='right' w='100%'>
                            <PriceBlock flexDirection='column' rate={rates[0]} isMutuaUser={isMutuaUser} />
                        </Flex>
                    </Box>}
                </Flex>
                {(rates.length !== 1) && <RateList
                  carId={car.id}
                  rates={rates}
                  onClick={onClick}
                  vehicleId={car.id} />}
            </Flex>
        </ContentMobile>
        <Modal padding='1em' isShown={!!linkModalInfo} width='1100px' onClose={() => onCloseModalInfo()} overflow='hidden'>
            <Flex width='100%' alignItems='center' justifyContent='center'>
                <iframe height={700} width={1050} loading='lazy' src={linkModalInfo} />
            </Flex>
        </Modal>
    </Container>
}

Card.propTypes = {
    id: PropTypes.any,
    freeCancel: PropTypes.bool,
    car: PropTypes.shape({
        id: PropTypes.any,
        type: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        features: PropTypes.shape({
            oilType: PropTypes.string,
            gear: PropTypes.string,
            doors: PropTypes.number,
            seats: PropTypes.number,
            suitcase: PropTypes.number,
        }),
        rates: PropTypes.arrayOf(
            PropTypes.shape({
                exclusive: PropTypes.bool,
                price: PropTypes.shape({
                    current: PropTypes.string,
                    amount: PropTypes.number,
                }),
                displayPrice: PropTypes.string,
                features: PropTypes.arrayOf(PropTypes.string),
                additionalInfoUrl: PropTypes.string,
            })
        ),
    }),
    seller: PropTypes.shape({
        name: PropTypes.string,
        link: PropTypes.string,
        image: PropTypes.string,
    }),
    onClick: PropTypes.func,
    place: PropTypes.shape({
        distance: PropTypes.number,
        name: PropTypes.string,
        originStationName: PropTypes.string,
    }),
    isMutuaUser: PropTypes.bool,
    setOnCloseModal: PropTypes.func,
    screenViewRollBack: PropTypes.func,
}

Card.defaultProps = {
    onClick: () => { },
    setOnCloseModal: () => { },
    screenViewRollBack: () => { },
    freeCancel: false,
}

export default Card
